/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessage } from 'react-intl'

import { FormattedMessage } from '@modules/i18n'
import { roleBadgeLabels } from '@modules/role-assignments-lib/messages'
import { CUSTOM_ROLE_BADGE_LABEL } from '@modules/role-assignments-lib/types'

import ROLE_BADGES_MODULES from '../resources'

import type { ReactNode } from 'react'
import type { MessageDescriptor } from 'react-intl'
import type { RoleBadgeInfo } from '.'

export const getTooltipMessage = (
  roleBadgeInfo: RoleBadgeInfo,
): MessageDescriptor & {
  values?: Record<string, string | number>
} => {
  const { roleLabel, resourceType, count } = roleBadgeInfo

  const isRoleAppliedToAllInstances = count === 'all'

  // tooltip is not necessary if there is no instance type and all is not true
  if (!resourceType && !isRoleAppliedToAllInstances) {
    return { ...roleBadgeLabels[roleLabel] }
  }

  const foundRoleBadgeModule = ROLE_BADGES_MODULES.find((module) => module.type === resourceType)

  if (isRoleAppliedToAllInstances) {
    if (!resourceType) {
      return defineMessage({
        id: 'role-badges.tooltip.all-resource-types',
        defaultMessage: 'This role applies to all current and future cloud resources.',
      })
    }

    return {
      ...foundRoleBadgeModule?.allInstancesRoleBadgeTooltipMessages,
    }
  }

  if (roleBadgeInfo.roleLabel === CUSTOM_ROLE_BADGE_LABEL) {
    return {
      ...foundRoleBadgeModule?.customRoleBadgeTooltipMessages,
      values: {
        count: count || 0,
      },
    }
  }

  return {
    ...foundRoleBadgeModule?.specificInstancesRoleBadgeTooltipMessages,
    values: {
      count: count || 0,
    },
  }
}

export const getCountLabel = (count?: number | 'all'): ReactNode => {
  if (count === 'all') {
    return <FormattedMessage id='role-badges.notification.all-instances' defaultMessage='All' />
  }

  return count
}
